/**
 * Fiscal Receipts admin sayfası — YN ÖKC yazarkasa-POS mali fişleri.
 *
 * Cihaz mali fişi basar, agent backend'e bildirir, burada listelenir.
 * Z raporu (gün sonu kapanış) ayrı tab.
 */
import { Card, Stack, Badge, Tabs, TextInput, Alert } from "@mantine/core";
import { IconSearch, IconAlertCircle } from "@tabler/icons-react";
import { useState } from "react";
import { useInfiniteResource } from "../../../shared/api";
import { PageHeader, MoneyDisplay, EmptyState, formatDateTime, InfiniteTable } from "../../../shared/ui";

const STATUS_COLOR: Record<string, string> = {
  PRINTED: "green",
  VOIDED: "red",
  PENDING_GIB: "yellow",
  REPORTED_GIB: "blue",
  FAILED: "red",
};

const TYPE_LABEL: Record<string, string> = {
  SALE: "Satış",
  REFUND: "İade",
  Z_REPORT: "Z Raporu",
  X_REPORT: "X Raporu",
};

export function FiscalReceiptsPage() {
  return (
    <Stack gap="md">
      <PageHeader
        title="Mali Fişler (YN ÖKC)"
        subtitle="Yazarkasa-POS cihazlarınızın bastığı GİB onaylı mali fişler"
      />
      <Alert color="blue" icon={<IconAlertCircle size={18} />}>
        ℹ️ YN ÖKC (Yeni Nesil Ödeme Kaydedici Cihaz) cihazları mali fişi <strong>kendileri basar</strong> ve
        GİB'e <strong>otomatik raporlar</strong>. Bu listede bu fişlerin sistemdeki yansıması görünür —
        resmi belge cihazda saklanır.
      </Alert>

      <Tabs defaultValue="receipts">
        <Tabs.List>
          <Tabs.Tab value="receipts">Mali Fişler</Tabs.Tab>
          <Tabs.Tab value="z-reports">Z Raporları (Gün Sonu)</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="receipts" pt="md">
          <ReceiptsList />
        </Tabs.Panel>
        <Tabs.Panel value="z-reports" pt="md">
          <ZReportsList />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function ReceiptsList() {
  const [search, setSearch] = useState("");
  // NOT: fiscal-receipts backend search_fields gerekiyor — arama şimdilik
  // useInfiniteResource'a geçirilmiyor (backend SearchFilter desteği eklenince
  // `search`'i hook'a bağla). Arama kutusu UI tutarlılığı için korundu.
  const {
    items: list,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteResource<any>("fiscal-receipts");

  return (
    <Stack gap="sm">
      <TextInput
        placeholder="Fiş no, ETTN veya cihaz seri no ara..."
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />

      <Card padding="sm" radius="md" withBorder>
        <InfiniteTable
          rows={list}
          rowKey={(r) => r.id}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          emptyContent={
            <EmptyState
              title="Mali fiş yok"
              description="YN ÖKC cihaz tanımlanmadıysa veya henüz fiş basılmadıysa boş gözükür."
            />
          }
          columns={[
            { header: "Fiş No", render: (r) => <code style={{ fontSize: 11 }}>{r.fiscal_number}</code> },
            { header: "Tarih", render: (r) => formatDateTime(r.printed_at) },
            { header: "Tip", render: (r) => <Badge variant="light" size="sm">{TYPE_LABEL[r.receipt_type] || r.receipt_type}</Badge> },
            { header: "Cihaz", render: (r) => r.device_name },
            { header: "ETTN", render: (r) => <code style={{ fontSize: 10 }}>{r.ettn?.substring(0, 16) || "-"}</code> },
            { header: "Z No", render: (r) => r.z_report_no || "-" },
            {
              header: "Ödeme",
              render: (r) => (
                <>
                  {r.payment_method}
                  {r.card_last_4 && <span style={{ marginLeft: 4, opacity: 0.7 }}>**** {r.card_last_4}</span>}
                </>
              ),
            },
            { header: "KDV", align: "right", render: (r) => <span style={{ color: "var(--mantine-color-dimmed)" }}><MoneyDisplay minor={r.vat_total_minor} /></span> },
            { header: "Toplam", align: "right", render: (r) => <MoneyDisplay minor={r.total_minor} fw={700} /> },
            { header: "Durum", render: (r) => <Badge color={STATUS_COLOR[r.status]} size="sm">{r.status}</Badge> },
          ]}
        />
      </Card>
    </Stack>
  );
}

function ZReportsList() {
  const {
    items: list,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteResource<any>("fiscal-z-reports");

  return (
    <Card padding="sm" radius="md" withBorder>
      <InfiniteTable
        rows={list}
        rowKey={(z) => z.id}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        emptyContent={
          <EmptyState
            title="Z raporu yok"
            description="YN ÖKC cihazı her gün sonu Z raporu basar; raporlar otomatik buraya yansır."
          />
        }
        columns={[
          { header: "Tarih", render: (z) => <span style={{ fontWeight: 500 }}>{z.date}</span> },
          { header: "Z No", render: (z) => <Badge>{z.z_number}</Badge> },
          { header: "Cihaz", render: (z) => z.device_name },
          { header: "Fiş Sayısı", align: "right", render: (z) => z.receipt_count },
          { header: "Toplam Satış", align: "right", render: (z) => <MoneyDisplay minor={z.total_sales_minor} fw={700} /> },
          {
            header: "İade",
            align: "right",
            render: (z) => (
              <span style={{ color: z.total_refunds_minor ? "var(--mantine-color-red-6)" : "var(--mantine-color-dimmed)" }}>
                <MoneyDisplay minor={z.total_refunds_minor} />
              </span>
            ),
          },
          { header: "💵 Nakit", align: "right", render: (z) => <MoneyDisplay minor={z.cash_total_minor} /> },
          { header: "💳 Kart", align: "right", render: (z) => <MoneyDisplay minor={z.card_total_minor} /> },
          { header: "🍴 Yemek Kartı", align: "right", render: (z) => <MoneyDisplay minor={z.meal_card_total_minor} /> },
          {
            header: "GİB Bildirim",
            render: (z) =>
              z.gib_reported_at
                ? <Badge color="green" size="sm">✓ Bildirildi</Badge>
                : <Badge color="yellow" size="sm">Bekleniyor</Badge>,
          },
        ]}
      />
    </Card>
  );
}
